<template>
  <div
    :class="cn(['', `--${display}`, `--${!!currentSlide.subtitle ? 'with-subtitle' : 'no-subtitle'}`])"
    v-if="slides && slides.length"
  >
    <a
      v-if="currentSlide.link_url"
      :key="'link-' + currentSlideIndex"
      :class="cn('cover')"
      :href="currentSlide.link_url"
    >
    </a>

    <div :class="cn('pager')">
      <button
        :class="cn(['pager-button', 'pager-button--prev'])"
        type="button"
        @click="onPrev"
        aria-label="Previous slide"
      >
        <span :class="cn('icon')" v-html="chevronSvg"></span>
      </button>

      <span :class="cn('pager-count')">{{ currentSlideIndex + 1 }} / {{ totalSlides }}</span>

      <button
        :class="cn(['pager-button', 'pager-button--next'])"
        type="button"
        @click="onNext"
        aria-label="Next slide"
      >
        <span :class="cn(['icon', 'icon--next'])" v-html="chevronSvg"></span>
      </button>
    </div>

    <div :class="cn('inner')">
      <div
        v-if="currentSlide"
        :key="'caption-' + currentSlideIndex"
        :class="cn('title')"
        v-html="currentSlide.title"
      ></div>
      <a
        v-if="currentSlide.link_url"
        :key="'link-' + currentSlideIndex"
        :class="cn('link')"
        :href="currentSlide.link_url"
      >
        {{ currentSlide.link_title }}
      </a>
    </div>
    <div
      v-if="currentSlide.subtitle"
      :key="'caption-' + currentSlideIndex"
      :class="cn('subtitle')"
      v-html="currentSlide.subtitle"
    ></div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useSlideshowInject } from '../../context/useSlideshowContext'
import { bem } from '../../utils/classnames'
import { HomeSectionSlideshowSlide } from './HomeSectionSlideshow.vue'

const cn = bem('home-section-slideshow-caption')

const props = defineProps<{
  slides: HomeSectionSlideshowSlide[]
  display: string
}>()

// Use the slideshow context (provided by CommonSlideshow) to get slider state.
const { emblaApi, index: currentSlideIndex, total } = useSlideshowInject()

const totalSlides = computed(() => total.value || props.slides.length)
const currentSlide = computed(() => props.slides[currentSlideIndex.value])

const chevronSvg = `
<svg viewBox="0 0 5 8" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M4.03668 7.16119L0.608111 3.73262L4.03668 0.304051" stroke="currentColor" stroke-width="0.86"/>
</svg>
`

const onPrev = () => {
  emblaApi.value?.scrollPrev()
}

const onNext = () => {
  emblaApi.value?.scrollNext()
}
</script>

<style lang="scss">
@import '~styles/base.scss';

$cn: 'home-section-slideshow-caption';

.#{$cn} {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 0 32px;
  position: relative;
  background: transparent;
  position: absolute;
  top: calc(100% + 8px);
  width: 100%;
  line-height: 14px;

  @include breakpoint('l') {
    gap: 0 16px;
  }

  &--left,
  &--right {
    gap: 0 16px !important;
  }

  &--with-subtitle {
    .#{$cn} {
      &__title {
        @include type-xs-uppercase;
      }
      &__link {
        @include transitionLink;
        opacity: 0;
      }
    }

    @include hover {
      .#{$cn}__link {
        opacity: 1;
      }
    }
  }

  &--no-subtitle {
    .#{$cn}__link {
      @include transitionLink;
    }

    &:not(:has(.#{$cn}__pager:hover)) {
      @include hover {
        .#{$cn}__link {
          color: var(--color-midgrey);
        }
      }
    }
  }

  &__inner {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    column-gap: 16px;
  }

  &__pager {
    min-width: 48px;
    margin-bottom: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
    z-index: 2;
  }

  &__pager-button {
    padding: 0px;
    line-height: 0;
    cursor: pointer;
  }

  &__pager-count {
    white-space: nowrap;
  }

  &__icon {
    display: inline-flex;
    width: 12px;
    height: 8px;

    svg {
      width: 100%;
      height: 100%;
      display: block;
    }

    &--next {
      transform: rotate(180deg);
      padding-top: 1px;
    }

    &--prev {
      padding-bottom: 1px;
    }
  }

  &__title {
    flex: 1;
  }

  &__link {
    text-align: right;
    @include transitionLink;

    @include hover {
      color: var(--color-midgrey);
    }
  }

  &__cover {
    @include cover;
  }

  &__subtitle {
    grid-column: 1 / -1;
  }
}
</style>
